import React, { useState } from 'react';
import {
  Activity, Check, CheckCircle2, ChevronDown, ChevronUp,
  Clock, Dumbbell, ExternalLink, Eye, Flame, Layers,
  Repeat, RotateCcw, ShieldCheck, Sparkles, Video
} from 'lucide-react';
import { ExerciseProgram, ProgramDay, ProgramDayExercise, DailyRecord, Exercise, ScheduledOccurrence } from '../types';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { normalizeExerciseDigits } from '../lib/exerciseTranslations';
import ExerciseDetailModal from './ExerciseDetailModal';

interface TodayRehabPlanCardProps {
  program: ExerciseProgram | null;
  day: ProgramDay | null;
  allDays?: ProgramDay[];
  currentDate: string;
  onSelectDay?: (day: ProgramDay) => void;
  record: DailyRecord;
  onUpdateRecord: (updated: DailyRecord) => void;
  onToggleExerciseCompletion: (exercise: ProgramDayExercise, index: number) => void;
  onNavigateTab: (tabId: string) => void;
  availableExercises?: Exercise[];
  matchedOccurrence?: ScheduledOccurrence | null;
  onToggleOccurrence?: (occ: ScheduledOccurrence) => void;
  defaultExpanded?: boolean;
}

export default function TodayRehabPlanCard({
  program,
  day,
  allDays = [],
  currentDate,
  onSelectDay,
  record,
  onUpdateRecord,
  onToggleExerciseCompletion,
  onNavigateTab,
  availableExercises = [],
  matchedOccurrence,
  onToggleOccurrence,
  defaultExpanded = true
}: TodayRehabPlanCardProps) {
  const { lang, t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const [selectedExerciseForModal, setSelectedExerciseForModal] = useState<Exercise | ProgramDayExercise | null>(null);

  if (!program && !day) {
    return null;
  }

  const exercises = day?.exercises || [];
  const totalCount = exercises.length;

  // Calculate completion count
  const completedCount = exercises.filter((ex, idx) => {
    if (ex.isCompleted) return true;
    const key = ex.id || ex.exerciseId;
    if (key && record.rehabCompletedIds?.includes(key)) return true;
    return false;
  }).length;

  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const isAllDone = totalCount > 0 && completedCount === totalCount;

  // Helper to resolve exercise metadata with availableExercises library
  const getResolvedExercise = (ex: ProgramDayExercise): Exercise => {
    const match = availableExercises.find(a =>
      (ex.exerciseId && a.id === ex.exerciseId) ||
      (ex.id && a.id === ex.id) ||
      (a.name && ex.name && a.name.trim().toLowerCase() === ex.name.trim().toLowerCase())
    );

    if (!match) {
      return {
        id: ex.id || ex.exerciseId || `ex_${Date.now()}`,
        name: ex.name,
        category: ex.category || 'rehab',
        description: ex.description || '',
        purpose: ex.purpose || '',
        targetMuscles: ex.targetMuscles || '',
        sets: ex.sets || 3,
        reps: ex.reps || '10',
        rest: ex.rest || '20',
        duration: ex.duration || 5,
        difficulty: ex.difficulty || 'Beginner',
        precautions: ex.precautions || '',
        equipment: ex.equipment || '',
        image: ex.image,
        videoUrl: ex.videoUrl,
        scope: ex.scope || 'global',
        isCustom: ex.isCustom || false
      };
    }

    return {
      ...match,
      name: ex.name || match.name,
      description: ex.description || match.description || '',
      purpose: ex.purpose || match.purpose || '',
      targetMuscles: ex.targetMuscles || match.targetMuscles || '',
      sets: ex.sets !== undefined ? ex.sets : match.sets,
      reps: ex.reps !== undefined ? ex.reps : match.reps,
      rest: ex.rest !== undefined ? ex.rest : match.rest,
      duration: ex.duration !== undefined ? ex.duration : match.duration,
      image: ex.image || match.image || (match as any).image_url,
      videoUrl: ex.videoUrl || match.videoUrl || (match as any).video_url
    };
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-emerald-200/80 dark:border-emerald-900/50 shadow-sm overflow-hidden transition-all">
      {/* Header Bar */}
      <div className="p-4 sm:p-5 bg-gradient-to-r from-emerald-50/70 via-teal-50/40 to-transparent dark:from-emerald-950/30 dark:via-teal-950/20 dark:to-transparent border-b border-gray-100 dark:border-gray-800">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Title & Info */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5 border border-emerald-200 dark:border-emerald-800">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm sm:text-base font-extrabold text-gray-900 dark:text-white">
                  {t("dashTodayExercisePlanTitle", "Today's Exercise Plan: Physiotherapy & Spine Rehab")}
                </h3>
                {isAllDone && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    {lang === 'fa' ? 'تکمیل شده' : 'Completed'}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 flex items-center gap-1.5 flex-wrap">
                <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                  {program?.title || t("rehabTitle", "Physiotherapy & Spine Rehab")}
                </span>
                {day?.title && (
                  <>
                    <span className="text-gray-300 dark:text-gray-600">•</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{day.title}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Controls: Expand/Collapse & Go to Rehab */}
          <div className="flex items-center gap-2 self-end sm:self-center">
            <button
              type="button"
              onClick={() => onNavigateTab('rehab')}
              className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-bold flex items-center gap-1 transition-colors cursor-pointer"
              title={t("dashManageInRehab", "Manage in Rehab Module")}
            >
              <span>{t("dashManageInRehab", "Manage in Rehab Module")}</span>
              <ExternalLink className="w-3 h-3" />
            </button>

            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
            >
              <span>
                {isExpanded
                  ? t("dashCollapseExercises", "Collapse exercises")
                  : t("dashViewExerciseList", "View day's exercises ({{count}} moves)", { count: formatNumberByLang(totalCount, lang) })}
              </span>
              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mt-3.5 pt-3 border-t border-emerald-100/80 dark:border-emerald-900/30">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-emerald-500" />
              {t("dashRehabProgress", "{{completed}} of {{total}} exercises completed ({{percent}}%)", {
                completed: formatNumberByLang(completedCount, lang),
                total: formatNumberByLang(totalCount, lang),
                percent: formatNumberByLang(progressPercent, lang)
              })}
            </span>
            <span className="text-[11px] font-mono font-bold text-emerald-600 dark:text-emerald-400">
              {formatNumberByLang(progressPercent, lang)}%
            </span>
          </div>

          <div className="w-full bg-gray-200 dark:bg-gray-700 h-2.5 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 rounded-full ${
                isAllDone ? 'bg-emerald-500 shadow-xs' : 'bg-emerald-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {isAllDone && (
            <div className="mt-2.5 p-2.5 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-xl flex items-center gap-2 text-xs font-bold text-emerald-800 dark:text-emerald-300 animate-in fade-in duration-300">
              <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span>{t("dashAllExercisesDone", "🎉 Excellent! All scheduled exercises for today are completed.")}</span>
            </div>
          )}
        </div>

        {/* Program Days Switcher (if program has multiple days) */}
        {allDays.length > 1 && (
          <div className="flex items-center gap-1.5 mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800/80 overflow-x-auto pb-1 no-scrollbar">
            <span className="text-[11px] text-gray-400 font-medium shrink-0">
              {lang === 'fa' ? 'روزهای برنامه:' : 'Program Days:'}
            </span>
            {allDays.map((d) => {
              const isSelected = day?.id === d.id;
              const dCompleted = (d.exercises || []).filter(ex => ex.isCompleted || (ex.id && record.rehabCompletedIds?.includes(ex.id))).length;
              const dTotal = (d.exercises || []).length;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => onSelectDay && onSelectDay(d)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center gap-1.5 ${
                    isSelected
                      ? 'bg-emerald-600 text-white shadow-2xs'
                      : 'bg-gray-100 dark:bg-gray-700/60 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span>{d.title}</span>
                  {dTotal > 0 && (
                    <span className={`text-[10px] px-1 py-0.2 rounded-md ${
                      isSelected ? 'bg-emerald-700 text-emerald-100' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                    }`}>
                      {formatNumberByLang(dCompleted, lang)}/{formatNumberByLang(dTotal, lang)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Expanded Activities List */}
      {isExpanded && (
        <div className="p-4 sm:p-5 bg-gray-50/50 dark:bg-gray-900/30 space-y-3">
          {day?.notes && (
            <div className="p-3 bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-800/50 rounded-xl text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
              <span className="shrink-0 text-sm">💡</span>
              <div>
                <strong className="font-extrabold">{lang === 'fa' ? 'دستورالعمل و نکات روز:' : 'Day Notes:'}</strong>{' '}
                {day.notes}
              </div>
            </div>
          )}

          {exercises.length === 0 ? (
            <div className="p-6 text-center text-xs text-gray-400 dark:text-gray-500 italic border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
              {t("dashNoExercisesForToday", "No exercises defined for today's program.")}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {exercises.map((ex, idx) => {
                const resolved = getResolvedExercise(ex);
                const isExCompleted = Boolean(
                  ex.isCompleted ||
                  (ex.id && record.rehabCompletedIds?.includes(ex.id)) ||
                  (ex.exerciseId && record.rehabCompletedIds?.includes(ex.exerciseId))
                );

                return (
                  <div
                    key={idx}
                    className={`relative p-3.5 rounded-xl border transition-all flex items-start gap-3 shadow-2xs group ${
                      isExCompleted
                        ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800/80'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-700 hover:shadow-sm'
                    }`}
                  >
                    {/* Completion Checkbox */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleExerciseCompletion(ex, idx);
                      }}
                      className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all cursor-pointer mt-0.5 ${
                        isExCompleted
                          ? 'bg-emerald-600 border-emerald-600 text-white shadow-2xs'
                          : 'border-gray-300 dark:border-gray-600 hover:border-emerald-500 bg-white dark:bg-gray-700'
                      }`}
                      title={isExCompleted ? t('markAsIncomplete', 'ثبت به عنوان انجام نشده') : t('markAsCompleted', 'ثبت به عنوان انجام شده')}
                      aria-label={`Toggle completion for ${resolved.name}`}
                    >
                      {isExCompleted && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                    </button>

                    {/* Thumbnail image if available */}
                    {resolved.image ? (
                      <div
                        onClick={() => setSelectedExerciseForModal(resolved)}
                        className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 cursor-pointer group-hover:opacity-90 relative"
                        title={t("dashReviewExerciseDetails", "View details and video tutorial")}
                      >
                        <img
                          src={resolved.image}
                          alt={resolved.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          referrerPolicy="no-referrer"
                        />
                        {resolved.videoUrl && (
                          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                            <Video className="w-3.5 h-3.5 text-white drop-shadow" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        onClick={() => setSelectedExerciseForModal(resolved)}
                        className="w-9 h-9 rounded-lg bg-emerald-100/70 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0 cursor-pointer"
                        title={t("dashReviewExerciseDetails", "View details and video tutorial")}
                      >
                        <Dumbbell className="w-4 h-4" />
                      </div>
                    )}

                    {/* Exercise Details */}
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center justify-between gap-1.5">
                        <h4
                          onClick={() => setSelectedExerciseForModal(resolved)}
                          className={`text-xs md:text-sm font-extrabold truncate cursor-pointer transition-colors ${
                            isExCompleted
                              ? 'line-through text-gray-400 dark:text-gray-500'
                              : 'text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400'
                          }`}
                          title={resolved.name}
                        >
                          {resolved.name}
                        </h4>

                        <button
                          type="button"
                          onClick={() => setSelectedExerciseForModal(resolved)}
                          className="text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 p-1 cursor-pointer"
                          title={t("dashReviewExerciseDetails", "View details and video tutorial")}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* Volume Badges (Sets / Reps / Rest) */}
                      <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                        {resolved.sets !== undefined && (
                          <span className="px-1.5 py-0.2 rounded-md bg-gray-100 dark:bg-gray-700/80 text-gray-700 dark:text-gray-300 font-bold border border-gray-200 dark:border-gray-600">
                            {formatNumberByLang(resolved.sets, lang)} {lang === 'fa' ? 'ست' : 'sets'}
                          </span>
                        )}

                        {resolved.reps && (
                          <span className="px-1.5 py-0.2 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-bold border border-emerald-200 dark:border-emerald-800">
                            {normalizeExerciseDigits(resolved.reps, lang)}
                          </span>
                        )}

                        {resolved.rest && (
                          <span className="px-1.5 py-0.2 rounded-md bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 font-medium border border-teal-200 dark:border-teal-800" dir="ltr">
                            ⏱️ {normalizeExerciseDigits(resolved.rest, lang)}
                          </span>
                        )}

                        {resolved.duration && resolved.duration > 0 && (
                          <span className="text-[10px] text-gray-400 font-mono">
                            ~{formatNumberByLang(resolved.duration, lang)} {lang === 'fa' ? 'دقیقه' : 'min'}
                          </span>
                        )}
                      </div>

                      {/* Target muscles or purpose snippet */}
                      {(resolved.targetMuscles || resolved.purpose) && (
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                          🎯 {resolved.targetMuscles || resolved.purpose}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal for full exercise review with instructions & video */}
      <ExerciseDetailModal
        isOpen={Boolean(selectedExerciseForModal)}
        onClose={() => setSelectedExerciseForModal(null)}
        exercise={selectedExerciseForModal}
      />
    </div>
  );
}
